/**
 * Memo Utilities and Helpers
 *
 * Centralized utility functions for memo management
 */
import { MemoStatus, MEMO_STATUS_LABELS, MEMO_STATUS_COLORS } from '../types/memo';
import { RoleLevel } from '../types/rbacImproved';
import { DEFAULT_ROLE_MARKUPS } from '../types/memoConfig';
/**
 * Calculate statistics from memo requests
 */
export const calculateMemoStatistics = (memos) => {
    const byStatus = {
        [MemoStatus.PENDING]: 0,
        [MemoStatus.APPROVED]: 0,
        [MemoStatus.SHIPPED]: 0,
        [MemoStatus.WITH_CLIENT]: 0,
        [MemoStatus.RETURNED]: 0,
        [MemoStatus.SOLD]: 0,
        [MemoStatus.CANCELLED]: 0,
        [MemoStatus.REJECTED]: 0
    };
    let totalValue = 0;
    let overdueCount = 0;
    const now = new Date();
    memos.forEach(memo => {
        byStatus[memo.status]++;
        totalValue += memo.diamondDetails.priceUSD || 0;
        // Check if overdue
        if (memo.dueDate && memo.status === MemoStatus.WITH_CLIENT) {
            const dueDate = new Date(memo.dueDate);
            if (dueDate < now) {
                overdueCount++;
            }
        }
    });
    return {
        total: memos.length,
        byStatus,
        totalValue,
        overdueCount
    };
};
/**
 * Filter memos based on user role
 */
export const filterMemosByRole = (memos, userId, roleLevel) => {
    // Admins see all memos
    if (roleLevel >= RoleLevel.SALES_MANAGER) {
        return memos;
    }
    // Dealers only see their own memos
    return memos.filter(memo => memo.dealerId === userId);
};
/**
 * Get memo status badge color
 */
export const getMemoStatusColor = (status) => {
    return MEMO_STATUS_COLORS[status];
};
/**
 * Get memo status label
 */
export const getMemoStatusLabel = (status) => {
    return MEMO_STATUS_LABELS[status];
};
/**
 * Check if memo is overdue
 */
export const isMemoOverdue = (memo) => {
    if (!memo.dueDate || memo.status !== MemoStatus.WITH_CLIENT) {
        return false;
    }
    const dueDate = new Date(memo.dueDate);
    const now = new Date();
    return dueDate < now;
};
/**
 * Calculate days remaining for memo
 */
export const getDaysRemaining = (memo) => {
    if (!memo.dueDate)
        return null;
    const dueDate = new Date(memo.dueDate);
    const now = new Date();
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
};
/**
 * Get available status transitions based on current status and role
 */
export const getAvailableStatusTransitions = (currentStatus, roleLevel) => {
    // Dealer transitions
    if (roleLevel < RoleLevel.SALES_MANAGER) {
        switch (currentStatus) {
            case MemoStatus.PENDING:
                return [MemoStatus.CANCELLED]; // Can cancel pending requests
            case MemoStatus.APPROVED:
            case MemoStatus.SHIPPED:
                return []; // Cannot change once approved/shipped
            case MemoStatus.WITH_CLIENT:
                return [MemoStatus.RETURNED]; // Can mark as returned
            default:
                return [];
        }
    }
    // Admin transitions
    switch (currentStatus) {
        case MemoStatus.PENDING:
            return [MemoStatus.APPROVED, MemoStatus.REJECTED];
        case MemoStatus.APPROVED:
            return [MemoStatus.SHIPPED, MemoStatus.CANCELLED];
        case MemoStatus.SHIPPED:
            return [MemoStatus.WITH_CLIENT];
        case MemoStatus.WITH_CLIENT:
            return [MemoStatus.RETURNED, MemoStatus.SOLD];
        case MemoStatus.RETURNED:
            return []; // Terminal state
        case MemoStatus.SOLD:
            return []; // Terminal state
        default:
            return [];
    }
};
/**
 * Format memo duration (how long it's been active)
 */
export const formatMemoDuration = (memo) => {
    const start = new Date(memo.requestedAt);
    const end = memo.returnedAt ? new Date(memo.returnedAt) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0)
        return 'Today';
    if (diffDays === 1)
        return '1 day';
    return `${diffDays} days`;
};
/**
 * Get memo risk level (for admins)
 */
export const getMemoRiskLevel = (memo) => {
    const value = memo.diamondDetails.priceUSD;
    const daysRemaining = getDaysRemaining(memo);
    // Overdue
    if (daysRemaining !== null && daysRemaining < 0) {
        return 'critical';
    }
    // High value + approaching due date
    if (value > 50000 && daysRemaining !== null && daysRemaining <= 3) {
        return 'high';
    }
    // High value or approaching due date
    if (value > 50000 || (daysRemaining !== null && daysRemaining <= 7)) {
        return 'medium';
    }
    return 'low';
};
/**
 * Sort memos by priority (for dashboard display)
 */
export const sortMemosByPriority = (memos) => {
    return [...memos].sort((a, b) => {
        // Overdue first
        const aOverdue = isMemoOverdue(a);
        const bOverdue = isMemoOverdue(b);
        if (aOverdue && !bOverdue)
            return -1;
        if (!aOverdue && bOverdue)
            return 1;
        // Then by status priority
        const statusPriority = {
            [MemoStatus.PENDING]: 1,
            [MemoStatus.WITH_CLIENT]: 2,
            [MemoStatus.SHIPPED]: 3,
            [MemoStatus.APPROVED]: 4,
            [MemoStatus.RETURNED]: 5,
            [MemoStatus.SOLD]: 6,
            [MemoStatus.REJECTED]: 7,
            [MemoStatus.CANCELLED]: 8
        };
        const aPriority = statusPriority[a.status];
        const bPriority = statusPriority[b.status];
        if (aPriority !== bPriority)
            return aPriority - bPriority;
        // Then by value (highest first)
        return (b.diamondDetails.priceUSD || 0) - (a.diamondDetails.priceUSD || 0);
    });
};
/**
 * Group memos by status for dashboard display
 */
export const groupMemosByStatus = (memos) => {
    const grouped = {
        [MemoStatus.PENDING]: [],
        [MemoStatus.APPROVED]: [],
        [MemoStatus.SHIPPED]: [],
        [MemoStatus.WITH_CLIENT]: [],
        [MemoStatus.RETURNED]: [],
        [MemoStatus.SOLD]: [],
        [MemoStatus.CANCELLED]: [],
        [MemoStatus.REJECTED]: []
    };
    memos.forEach(memo => {
        grouped[memo.status].push(memo);
    });
    return grouped;
};
/**
 * Check if user can request memo based on role
 * Basic Dealer and up can request memos (with 5% price increase)
 */
export const canRequestMemo = (roleLevel) => {
    return roleLevel >= RoleLevel.BASIC_DEALER;
};
/**
 * Check if user can approve memos
 */
export const canApproveMemo = (roleLevel) => {
    return roleLevel >= RoleLevel.SALES_MANAGER;
};
/**
 * Check if user can manage all memos (admin view)
 */
export const canManageAllMemos = (roleLevel) => {
    return roleLevel >= RoleLevel.SALES_MANAGER;
};
/**
 * Calculate memo price with markup
 * Supports role-based and user-specific markups
 *
 * @param basePrice - Diamond base price
 * @param roleLevel - User's role level
 * @param userId - Optional user ID for user-specific markup
 * @param userMarkups - Optional array of user-specific markups
 */
export const calculateMemoPrice = (basePrice, roleLevel, userId, userMarkups) => {
    // Check for user-specific markup override first
    if (userId && userMarkups) {
        const userMarkup = userMarkups.find(m => m.userId === userId);
        if (userMarkup) {
            const markup = userMarkup.markupPercent / 100;
            console.log(`💰 User-specific markup: ${userMarkup.markupPercent}% for user ${userId}`);
            return basePrice * (1 + markup);
        }
    }
    // Fall back to role-based markup
    const roleMarkup = DEFAULT_ROLE_MARKUPS.find(m => m.roleLevel === roleLevel && m.enabled);
    if (roleMarkup && roleMarkup.markupPercent > 0) {
        const markup = roleMarkup.markupPercent / 100;
        console.log(`💰 Role markup: ${roleMarkup.markupPercent}% for ${roleMarkup.roleName}`);
        return basePrice * (1 + markup);
    }
    console.log(`💰 No markup applied for role level ${roleLevel}`);
    return basePrice;
};
/**
 * Get memo price markup percentage
 *
 * @param roleLevel - User's role level
 * @param userId - Optional user ID for user-specific markup
 * @param userMarkups - Optional array of user-specific markups
 */
export const getMemoMarkupPercent = (roleLevel, userId, userMarkups) => {
    // Check for user-specific markup override
    if (userId && userMarkups) {
        const userMarkup = userMarkups.find(m => m.userId === userId);
        if (userMarkup) {
            return userMarkup.markupPercent;
        }
    }
    // Fall back to role-based markup
    const roleMarkup = DEFAULT_ROLE_MARKUPS.find(m => m.roleLevel === roleLevel && m.enabled);
    return roleMarkup?.markupPercent || 0;
};
export const MemoHelpers = {
    calculateMemoStatistics,
    filterMemosByRole,
    getMemoStatusColor,
    getMemoStatusLabel,
    isMemoOverdue,
    getDaysRemaining,
    getAvailableStatusTransitions,
    formatMemoDuration,
    getMemoRiskLevel,
    sortMemosByPriority,
    groupMemosByStatus,
    canRequestMemo,
    canApproveMemo,
    canManageAllMemos,
    calculateMemoPrice,
    getMemoMarkupPercent
};
export default MemoHelpers;
